import moment from 'moment';
import { ByWeekday, Frequency, RRule } from 'rrule';
import {
  AlertThresholdKind,
  IAlertThreshold,
  IAlertThresholdInput,
  IScheduleTypeLimit,
  ScheduleType,
  TimeFormat,
} from '../../../utils/apollo/graphqlSchema';
import { globalFormat } from '../../../utils/formatting';
import { assertNotNil } from '../../../utils/types';
import { UnreachableCaseError } from 'ts-essentials';

export const ordinalSuffixOf = (i: number) => {
  const j = i % 10;
  const k = i % 100;
  if (j === 1 && k !== 11) {
    return i + 'st';
  }
  if (j === 2 && k !== 12) {
    return i + 'nd';
  }
  if (j === 3 && k !== 13) {
    return i + 'rd';
  }
  return i + 'th';
};

export const buildScheduleLimitMessage = (
  scheduleLimit: IScheduleTypeLimit,
) => {
  let type = '';
  switch (scheduleLimit.type) {
    case ScheduleType.Analysis:
      type = 'analysis';
      break;
    case ScheduleType.BrazeCohortImport:
      type = 'cohort';
      break;
    case ScheduleType.Dashboard:
      type = 'dashboard';
      break;
    default:
      throw new UnreachableCaseError(scheduleLimit.type);
  }

  if (scheduleLimit.maxCount) {
    return `${scheduleLimit.maxCount} ${type}`;
  }

  return type;
};

const generateTimeStr = (hour: number, minute: number) => {
  const { timeFormat } = globalFormat.getGlobalFormat();
  const format = timeFormat === TimeFormat.TwelveHour ? 'h:mma' : 'H:mm';
  return moment().hour(hour).minute(minute).format(format);
};

// return 'Every <interval> <frequency>s' if interval > 1,
// '<Frequency>ly' otherwise (Day -> Daily being special case)
const generatePrefix = (
  frequencyWithCapitalLetter: string,
  interval: number | undefined,
) => {
  if (interval !== undefined && interval > 1) {
    return `Every ${interval} ${frequencyWithCapitalLetter.toLowerCase()}s`;
  }
  return frequencyWithCapitalLetter === 'Day'
    ? 'Daily'
    : `${frequencyWithCapitalLetter}ly`;
};

const generateDailyString = (interval: number | undefined, timeStr: string) => {
  return `${generatePrefix('Day', interval)} ${timeStr}`;
};

// XXX these are defined in RRule and the index of each day matches the js/moment week day numbers
const RRULE_WEEK_DAYS_SHORT = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const weekDayToStr = (weekDay: ByWeekday) => {
  const wd =
    typeof weekDay === 'string'
      ? RRULE_WEEK_DAYS_SHORT.indexOf(weekDay)
      : typeof weekDay === 'number'
        ? weekDay
        : weekDay.getJsWeekday();
  return moment().day(wd).format('dddd');
};

const generateWeeklyString = (
  interval: number | undefined,
  timeStr: string,
  daysOfWeek: ByWeekday | ByWeekday[],
) => {
  const days = Array.isArray(daysOfWeek) ? daysOfWeek : [daysOfWeek];
  const daysStr = days.map(weekDayToStr).join(', ');
  return `${generatePrefix('Week', interval)} on ${daysStr} ${timeStr}`;
};

const generateMonthlyString = (
  interval: number | undefined,
  timeStr: string,
  dayOfMonth: number | number[] | null | undefined,
  byWeekDay: ByWeekday | ByWeekday[] | null | undefined,
) => {
  // XXX currently we only get a single dayOfMonth OR byWeekDay spec here
  // yet the logic below can work with multiple days or week days specs.

  const prefix = `${generatePrefix('Month', interval)} on`;

  if (dayOfMonth) {
    // plain monthly schedule, specifying day of the month
    const days = Array.isArray(dayOfMonth) ? dayOfMonth : [dayOfMonth];
    const daysStr = days.map(ordinalSuffixOf).join(', ');
    return `${prefix} ${daysStr} day${days.length > 1 ? 's' : ''} ${timeStr}`;
  }

  // A monthly schedule specified as the "Nth weekday of the month".
  assertNotNil(byWeekDay);
  const weekDays = Array.isArray(byWeekDay) ? byWeekDay : [byWeekDay];

  const myWeekDayStr = (wd: ByWeekday) => {
    const n = (typeof wd === 'object' ? wd.n : undefined) ?? 1;
    const wds = weekDayToStr(wd);
    return `${ordinalSuffixOf(n)} ${wds}`;
  };

  const weekDaysStr = weekDays.map(myWeekDayStr).join(', ');
  return `${prefix} ${weekDaysStr} ${timeStr}`;
};

// The rrule.toText() function does not meet our requirements,
// implement our own (somewhat limited) version
export const generateRuleString = (rrule: RRule): string => {
  const {
    freq, // an enum value, we only use the subset in the switch below
    interval, // the X in "Every X"
    // hour (0-23)
    byhour,
    // minute (0-59), or in our case - (0,15,30,45)
    byminute,
    // day of month, only relevant to Monthly,
    bymonthday,
    // weekly schedule details (days of week)
    byweekday,
  } = rrule.origOptions;

  // we only support a single hour/minute spec per schedule entry
  assertNotNil(byhour);
  const myHour = Array.isArray(byhour) ? byhour[0] : byhour;
  assertNotNil(byminute);
  const myMinute = Array.isArray(byminute) ? byminute[0] : byminute;
  const timeStr = generateTimeStr(myHour, myMinute);

  let ret;
  switch (freq) {
    case Frequency.MONTHLY:
      ret = generateMonthlyString(interval, timeStr, bymonthday, byweekday);
      break;
    case Frequency.WEEKLY:
      assertNotNil(byweekday);
      ret = generateWeeklyString(interval, timeStr, byweekday);
      break;
    case Frequency.DAILY:
      ret = generateDailyString(interval, timeStr);
      break;
    default: // should never happen until we start supporting other frequencies
      throw Error(`Invalid Frequency provided: ${freq}`);
  }
  return ret;
};

export const alertThresholdToInput = (
  at: IAlertThreshold,
): IAlertThresholdInput => {
  if (at.__typename === 'AlertThresholdCustom') {
    return {
      kind: at.kind,
      customThreshold: {
        compareOpKey: at.compareOp.key,
        compareValue: at.compareValue,
        valueIsPercent: at.valueIsPercent,
      },
      logicOp: at.logicOp?.key ?? null,
    };
  } else {
    return {
      kind: AlertThresholdKind.Anomaly,
      customThreshold: null,
      logicOp: null,
    };
  }
};
